import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings as SettingsIcon, ShieldAlert, RotateCcw, Trash2, Eraser, Server, Store, Save, UserCircle, ShieldCheck, Printer, CreditCard, Building2, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Card, Loading, PageHeader, Button, Modal, Badge } from "@/components/common";

export default function Settings() {
  const { user, isOwner, can } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetMode, setResetMode] = useState(null); // "orders" | "factory"
  const [cleanMode, setCleanMode] = useState(false);

  useEffect(() => {
    api.get("/system/health").then(setHealth).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader title="Pengaturan" subtitle={user?.name} icon={SettingsIcon} />

      {can("settings", "view") && (
        <Card className="p-5 mb-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><SettingsIcon size={17} /> Manajemen</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { to: "/users", label: "Karyawan", desc: "Kelola akun & email karyawan", icon: UserCircle },
              { to: "/roles", label: "Peran & Hak Akses", desc: "Atur role & permission", icon: ShieldCheck },
              { to: "/printer", label: "Printer Thermal", desc: "Bluetooth · USB · LAN · 58mm", icon: Printer },
              { to: "/payment-methods", label: "Metode Bayar", desc: "Tunai, transfer, QRIS, dll.", icon: CreditCard },
              { to: "/branches", label: "Cabang / Unit", desc: "Kelola toko & unit", icon: Building2 },
            ].map((m) => (
              <Link key={m.to} to={m.to} data-testid={`mgmt-${m.to.slice(1)}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition group">
                <span className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><m.icon size={18} /></span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-slate-800 text-sm">{m.label}</span>
                  <span className="block text-xs text-slate-400 truncate">{m.desc}</span>
                </span>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-primary shrink-0" />
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5 mb-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Server size={17} /> Status Database (Cloud)</h3>
        {health ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Info label="Status" value={<Badge className={health.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>{health.ok ? "Terhubung" : "Gagal"}</Badge>} />
            <Info label="Latensi" value={`${health.latency_ms} ms`} />
            <Info label="Database" value={health.db_name} />
            <Info label="Waktu Server" value={fmtDateTime(health.server_time)} />
            <Info label="Produk" value={health.counts?.products} />
            <Info label="Bahan" value={health.counts?.materials} />
            <Info label="Order" value={health.counts?.orders} />
            <Info label="Karyawan" value={health.counts?.users} />
          </div>
        ) : <p className="text-sm text-slate-400">Tidak dapat memuat status.</p>}
      </Card>

      {can("settings", "edit") && <StoreSettings />}

      {isOwner && (
        <Card className="p-5 border-rose-200">
          <h3 className="font-semibold text-rose-700 mb-1 flex items-center gap-2"><ShieldAlert size={18} /> Zona Berbahaya</h3>
          <p className="text-sm text-slate-500 mb-4">Aksi berikut hanya untuk Owner dan tidak dapat dibatalkan.</p>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div>
                <div className="font-medium text-slate-800 flex items-center gap-2"><RotateCcw size={16} /> Reset Order / Invoice</div>
                <p className="text-xs text-slate-500 mt-0.5">Hapus semua order, tugas perangkai, komisi, draft WA & piutang. Stok & master data tetap.</p>
              </div>
              <Button variant="outline" onClick={() => setResetMode("orders")} data-testid="reset-orders-btn">Reset Order</Button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-orange-50 border border-orange-200">
              <div>
                <div className="font-medium text-slate-800 flex items-center gap-2"><Eraser size={16} /> Bersihkan Data Test (Produk & Bahan)</div>
                <p className="text-xs text-slate-500 mt-0.5">Hapus semua produk/bahan uji coba. Item yang namanya mengandung kata kunci (mis. <b>anggrek</b>) dipertahankan. Stok, batch & mutasinya ikut dibersihkan.</p>
              </div>
              <Button variant="outline" onClick={() => setCleanMode(true)} data-testid="cleanup-data-btn">Bersihkan Data Test</Button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200">
              <div>
                <div className="font-medium text-slate-800 flex items-center gap-2"><Trash2 size={16} /> Factory Reset (Hapus Semua Data)</div>
                <p className="text-xs text-slate-500 mt-0.5">Hapus 100% data operasional & master. Akun, peran, cabang, metode bayar & pengaturan dipertahankan.</p>
              </div>
              <Button variant="danger" onClick={() => setResetMode("factory")} data-testid="factory-reset-btn">Factory Reset</Button>
            </div>
          </div>
        </Card>
      )}

      {resetMode && <ResetModal mode={resetMode} onClose={() => setResetMode(null)} />}
      {cleanMode && <CleanupModal onClose={() => setCleanMode(false)} />}
    </>
  );
}

function ResetModal({ mode, onClose }) {
  const isFactory = mode === "factory";
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/system/reset-preview").then(setPreview).catch(() => setPreview({ has_pin: false }));
  }, []);

  const submit = async () => {
    if (confirm.trim().toUpperCase() !== "RESET") return toast.error('Ketik "RESET" untuk konfirmasi');
    if (!secret) return toast.error(preview?.has_pin ? "Masukkan PIN Owner" : "Masukkan password Owner");
    setBusy(true);
    try {
      const body = { confirm: "RESET" };
      if (preview?.has_pin) body.pin = secret; else body.password = secret;
      const res = await api.post(isFactory ? "/system/factory-reset" : "/system/reset-orders", body);
      toast.success(`Berhasil — ${res.total || 0} data dihapus`);
      onClose();
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isFactory ? "Factory Reset" : "Reset Order / Invoice"} maxWidth="max-w-md">
      <div className="space-y-4">
        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 flex gap-2">
          <ShieldAlert size={18} className="shrink-0" />
          <span>Tindakan ini <b>permanen</b> dan tidak bisa dibatalkan. {isFactory ? "Semua data operasional & master akan dihapus." : "Semua order & invoice akan dihapus."}</span>
        </div>

        {isFactory && preview?.collections && Object.keys(preview.collections).length > 0 && (
          <div className="text-sm">
            <div className="text-xs font-semibold uppercase text-slate-400 mb-1">Data yang akan dihapus ({preview.total})</div>
            <div className="max-h-32 overflow-y-auto border border-slate-100 rounded-xl p-2 space-y-1">
              {Object.entries(preview.collections).map(([k, v]) => (
                <div key={k} className="flex justify-between text-slate-500"><span>{k}</span><span>{v}</span></div>
              ))}
            </div>
          </div>
        )}

        <label className="block">
          <span className="text-sm font-medium text-slate-600 mb-1 block">Ketik <b>RESET</b> untuk konfirmasi</span>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="reset-confirm" placeholder="RESET" />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-600 mb-1 block">{preview?.has_pin ? "PIN Owner" : "Password Owner"}</span>
          <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="reset-secret" />
        </label>

        <Button variant="danger" className="w-full" onClick={submit} disabled={busy} data-testid="reset-submit">
          {busy ? "Memproses..." : isFactory ? "Hapus Semua Data" : "Reset Order"}
        </Button>
      </div>
    </Modal>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-xs text-slate-400">{label}</div><div className="text-slate-700 font-medium mt-0.5">{value ?? "-"}</div></div>
);

function CleanupModal({ onClose }) {
  const [keyword, setKeyword] = useState("anggrek");
  const [opts, setOpts] = useState({ clean_products: true, clean_materials: true });
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadPreview = async () => {
    if (!keyword.trim()) return toast.error("Isi kata kunci yang dipertahankan");
    setLoadingPreview(true);
    try {
      const p = await api.get(`/system/cleanup-preview?keep=${encodeURIComponent(keyword.trim())}&clean_products=${opts.clean_products}&clean_materials=${opts.clean_materials}`);
      setPreview(p);
    } catch (e) { toast.error(e.message); } finally { setLoadingPreview(false); }
  };

  useEffect(() => { loadPreview(); }, []);

  const submit = async () => {
    if (confirm.trim().toUpperCase() !== "RESET") return toast.error('Ketik "RESET" untuk konfirmasi');
    if (!secret) return toast.error(preview?.has_pin ? "Masukkan PIN Owner" : "Masukkan password Owner");
    setBusy(true);
    try {
      const body = { confirm: "RESET", keep_keyword: keyword.trim(), clean_products: opts.clean_products, clean_materials: opts.clean_materials };
      if (preview?.has_pin) body.pin = secret; else body.password = secret;
      const res = await api.post("/system/cleanup-data", body);
      toast.success(`Berhasil — ${res.total || 0} data test dihapus, ${res.kept || 0} item dipertahankan`);
      onClose();
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Bersihkan Data Test" maxWidth="max-w-lg">
      <div className="space-y-4">
        <div className="p-3 rounded-xl bg-orange-50 border border-orange-200 text-sm text-orange-800 flex gap-2">
          <ShieldAlert size={18} className="shrink-0" />
          <span>Semua produk/bahan yang namanya <b>TIDAK</b> mengandung kata kunci akan dihapus <b>permanen</b> beserta stok, batch & mutasinya. Item yang namanya mengandung kata kunci dipertahankan.</span>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-600 mb-1 block">Pertahankan item yang namanya mengandung</span>
          <div className="flex gap-2">
            <input value={keyword} onChange={(e) => setKeyword(e.target.value)} className="flex-1 h-10 px-3 rounded-xl border border-slate-300" data-testid="cleanup-keyword" placeholder="anggrek" />
            <Button variant="outline" onClick={loadPreview} disabled={loadingPreview} data-testid="cleanup-preview-btn">{loadingPreview ? "..." : "Pratinjau"}</Button>
          </div>
        </label>

        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={opts.clean_materials} onChange={(e) => setOpts((o) => ({ ...o, clean_materials: e.target.checked }))} data-testid="cleanup-materials-chk" /> Bersihkan Bahan</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={opts.clean_products} onChange={(e) => setOpts((o) => ({ ...o, clean_products: e.target.checked }))} data-testid="cleanup-products-chk" /> Bersihkan Produk</label>
        </div>

        {preview && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="border border-rose-100 rounded-xl p-2">
              <div className="text-xs font-semibold uppercase text-rose-500 mb-1">Dihapus ({preview.total_delete})</div>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {Object.entries(preview.delete).flatMap(([, names]) => names).slice(0, 100).map((n, i) => <div key={i} className="text-slate-500 truncate">{n || "(tanpa nama)"}</div>)}
                {preview.total_delete === 0 && <div className="text-slate-400">Tidak ada</div>}
              </div>
            </div>
            <div className="border border-emerald-100 rounded-xl p-2">
              <div className="text-xs font-semibold uppercase text-emerald-600 mb-1">Dipertahankan ({preview.total_keep})</div>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {Object.entries(preview.keep).flatMap(([, names]) => names).slice(0, 100).map((n, i) => <div key={i} className="text-slate-500 truncate">{n || "(tanpa nama)"}</div>)}
                {preview.total_keep === 0 && <div className="text-slate-400">Tidak ada</div>}
              </div>
            </div>
          </div>
        )}

        <label className="block">
          <span className="text-sm font-medium text-slate-600 mb-1 block">Ketik <b>RESET</b> untuk konfirmasi</span>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="cleanup-confirm" placeholder="RESET" />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-600 mb-1 block">{preview?.has_pin ? "PIN Owner" : "Password Owner"}</span>
          <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="cleanup-secret" />
        </label>

        <Button variant="danger" className="w-full" onClick={submit} disabled={busy} data-testid="cleanup-submit">
          {busy ? "Memproses..." : `Hapus ${preview?.total_delete ?? ""} Data Test`}
        </Button>
      </div>
    </Modal>
  );
}

function StoreSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then(setS).catch(() => {}); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const keys = ["store_name", "store_address", "store_phone", "store_email", "store_instagram", "receipt_header", "receipt_footer", "receipt_paper_size", "negative_stock_policy"];
      const body = Object.fromEntries(keys.filter((k) => s[k] !== undefined && s[k] !== null).map((k) => [k, s[k]]));
      await api.patch("/settings", body);
      toast.success("Pengaturan disimpan");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <Card className="p-5 mb-6">
      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Store size={17} /> Info Toko, Struk & Printer</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <F label="Nama Toko"><input value={s.store_name || ""} onChange={(e) => set("store_name", e.target.value)} className="sinp" data-testid="store-name" /></F>
        <F label="Telepon"><input value={s.store_phone || ""} onChange={(e) => set("store_phone", e.target.value)} className="sinp" /></F>
        <F label="Alamat"><input value={s.store_address || ""} onChange={(e) => set("store_address", e.target.value)} className="sinp" /></F>
        <F label="Instagram"><input value={s.store_instagram || ""} onChange={(e) => set("store_instagram", e.target.value)} className="sinp" /></F>
        <F label="Header Struk"><input value={s.receipt_header || ""} onChange={(e) => set("receipt_header", e.target.value)} className="sinp" /></F>
        <F label="Footer Struk"><input value={s.receipt_footer || ""} onChange={(e) => set("receipt_footer", e.target.value)} className="sinp" /></F>
        <F label="Ukuran Kertas Struk"><select value={s.receipt_paper_size || "80mm"} onChange={(e) => set("receipt_paper_size", e.target.value)} className="sinp"><option value="80mm">80mm (thermal)</option><option value="80x40">80x40 (label)</option><option value="a4">A4</option></select></F>
        <F label="Kebijakan Stok Minus"><select value={s.negative_stock_policy || "warn"} onChange={(e) => set("negative_stock_policy", e.target.value)} className="sinp"><option value="block">Blokir</option><option value="warn">Peringatan</option><option value="approve">Perlu PIN Manager</option></select></F>
      </div>
      <Button className="mt-4" onClick={save} disabled={busy} data-testid="store-save"><Save size={16} /> {busy ? "Menyimpan..." : "Simpan Pengaturan"}</Button>
      <style>{`.sinp{width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #cbd5e1;border-radius:.75rem;font-size:.875rem;outline:none}.sinp:focus{border-color:hsl(var(--primary))}`}</style>
    </Card>
  );
}

const F = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">{label}</span>{children}</label>
);
